import { useEffect, useState } from 'react'
import { MapPin, X } from 'lucide-react'
import { api, type StampRegion } from '../services/api'
import { useI18n } from '../i18n/useI18n'
import { apiErrorI18nKey, apiErrorRef, isRetryable } from '../lib/apiErrorMessage'

interface StampRegionPickerProps {
  open: boolean
  onClose: () => void
  onSelect: (regionId: string) => void
}

/**
 * GPS 拒否/不可時の手動地域選択モーダル (#1126)。有効地域は現状 1 件（平塚）だが
 * 横展開でリストが伸びる前提の UI。選択は useStampRally 側で sessionStorage 永続。
 */
export default function StampRegionPicker({ open, onClose, onSelect }: StampRegionPickerProps) {
  const { t } = useI18n()
  // null = 未取得 (ローディング表示)。初回 open 時に取得し、以降はキャッシュ。
  const [regions, setRegions] = useState<StampRegion[] | null>(null)
  // **boolean ではなく例外そのものを持つ。** 401 と 500 では出すべき文言も、
  // 再読み込みが意味を持つかも違う。潰すと全部「読み込めませんでした」になる。
  const [failed, setFailed] = useState<unknown>(null)
  const loading = open && regions === null && !failed

  // 閉じるときに失敗状態を捨てる。残すと、通信が復帰した後に開き直しても
  // 再取得が走らないまま古いエラーが出続ける。
  const handleClose = () => {
    setFailed(null)
    onClose()
  }

  useEffect(() => {
    if (!open || regions !== null || failed) return
    let cancelled = false
    api
      .getStampRegions()
      .then((res) => {
        if (!cancelled) setRegions(res.regions)
      })
      .catch((e) => {
        // **空リストにしない。** 取得失敗を「地域が無い」と表示すると、
        // index 漏れ等でサーバが 500 を返しているのに「開催なし」に見え、
        // 誰も異常に気付けない (#1624 の議論)。失敗は失敗として出す。
        if (!cancelled) setFailed(e)
      })
    return () => {
      cancelled = true
    }
  }, [open, regions, failed])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40" onClick={handleClose}>
      <div
        className="w-full max-w-[430px] bg-white rounded-t-2xl p-5 pb-8 safe-bottom animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg text-navy">{t('stampRally.selectRegionTitle')}</h2>
          <button onClick={handleClose} className="p-1.5 text-text-tertiary tap-effect" aria-label="close">
            <X size={20} />
          </button>
        </div>
        {loading ? (
          <div className="py-8 flex justify-center">
            <div className="w-6 h-6 border-2 border-navy border-t-transparent rounded-full animate-spin" />
          </div>
        ) : failed ? (
          <div className="py-8 flex flex-col items-center gap-2">
            <p className="text-center text-sm font-body text-text-secondary">{t(apiErrorI18nKey(failed))}</p>
            <p className="text-center text-[10px] font-body text-text-tertiary">{apiErrorRef(failed)}</p>
            {isRetryable(failed) && (
              <button
                onClick={() => setFailed(null)}
                className="mt-1 rounded-xl bg-navy px-5 py-2 font-body text-sm text-white tap-effect"
              >
                {t('common.retry')}
              </button>
            )}
          </div>
        ) : (regions ?? []).length === 0 ? (
          <p className="py-8 text-center text-sm font-body text-text-secondary">
            {t('stampRally.noEvent')}
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {(regions ?? []).map((region) => (
              <button
                key={region.id}
                onClick={() => {
                  onSelect(region.id)
                  handleClose()
                }}
                className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3.5 text-left tap-card"
              >
                <MapPin size={18} className="text-yokoso-red shrink-0" />
                <span className="font-body text-text-primary">{region.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
